import type { FC } from "react";

import { useTranslations } from "@ui/providers/useTranslations";
import classNames from "classnames";

import { UiCheckIcon } from "@jti/brand-icons/ui-check";
import { Toggle } from "@jti/core-components";
import { Card } from "@jti/core-components/components/Card";

import { Controller, useFormContext } from "../Form";
import { InputIcon, TextInput } from "../Inputs";

import styles from "./ReferralCode.module.scss";

const REFERRAL_CODE_NAME = "referral_code";

type ReferralCodeProps = {
  fieldName?: string;
  isActive?: boolean;
  isCodeValid?: boolean;
  isCodeInvalid?: boolean;
  onToggle?: () => void;
  errorMessage?: string;
  isLocked?: boolean;
};
export const ReferralCode: FC<ReferralCodeProps> = ({
  isActive,
  isCodeInvalid,
  isCodeValid,
  onToggle,
  fieldName = REFERRAL_CODE_NAME,
  errorMessage,
  isLocked = false,
}) => {
  const { __ } = useTranslations();
  const { control } = useFormContext();
  return (
    <Card variant="level-1-club">
      <div className={classNames(styles.wrapper, { [styles.wrapperActive]: isActive })}>
        <div className={styles.headerWrapper}>
          <div className={styles.innerContainer}>
            <h5 id="label-refferalCodeToggle" className={styles.headerTitle}>
              {__("auth.loyalty.haveReferralCode")}
            </h5>
            <p className={styles.informationText}>{__("auth.loyalty.warningMessage")}</p>
          </div>
          <div className={styles.toggleSlot}>
            <Toggle
              onChange={() => onToggle?.()}
              data-testid="refferalCodeToggle"
              id="refferalCodeToggle"
              checked={isActive ?? false}
              disabled={isLocked}
              aria-labelledby="label-refferalCodeToggle"
            />
          </div>
        </div>
        {isActive && (
          <>
            <div className={styles.divider} />
            <div className={styles.innerContainer}>
              <p className={styles.informationText}>{__("auth.loyalty.description")}</p>
              <Controller
                name={fieldName}
                control={control}
                render={({ field }) => (
                  <TextInput
                    {...field}
                    type="text"
                    data-testid="refferalCodeInput"
                    isError={isCodeInvalid}
                    isSuccess={isCodeValid}
                    label={__("auth.loyalty.referralTitle")}
                    errorText={errorMessage}
                    helperText={__("auth.loyalty.inputDescription")}
                    successText={__("auth.loyalty.successMessage")}
                    placeholder={__("auth.loyalty.inputPlaceholder")}
                    elemAfter={
                      isCodeValid ? <InputIcon icon={UiCheckIcon} isSuccess isDisabled={isLocked} /> : undefined
                    }
                    readOnly={isLocked}
                  />
                )}
              />
            </div>
          </>
        )}
      </div>
    </Card>
  );
};
